import {createApp, type App as VueApp} from 'vue'
import {createGtm} from '@gtm-support/vue-gtm'
import {
  createBootstrap,
  BAlert, BCol, BRow, BLink, BModal, BFormSelect,
  BBadge, BCollapse, BButton, BSpinner, BDropdown, BDropdownItem, BDropdownText,
  BForm, BFormGroup, BFormInput, BFormCheckbox, BFormRadio, BFormTextarea,
  BFormRadioGroup, BFormInvalidFeedback, BImg, BTooltip, BPopover, BProgress,
  BPagination, BInputGroup, BInputGroupText,
  BNav, BNavItem, BNavText, BNavbar, BNavbarBrand, BNavbarNav, BNavbarToggle,
  BAvatar, BListGroup, BListGroupItem, BContainer, BTabs, BTab,
  vBToggle, vBTooltip, vBModal,
  BInput, BFormFile
} from 'bootstrap-vue-next'
import 'bootstrap-vue-next/dist/bootstrap-vue-next.css'
import oktaAuth from './okta'
import OktaVue from '@okta/okta-vue'
import store from './store'
import router from './router'
import {mockServer} from './server'
import '@/scss/custom.scss'
import App from './App.vue'
import appConfig from '@/config'
import MyMachine from './plugin'
import '@okta/okta-auth-js/polyfill'
import constants from '@/constants'
import BDropdownForm from '@/components/ui/BDropdownForm.vue'

const oktaPlugin: any = OktaVue
const oktaOptions: any = {oktaAuth}

const app: VueApp = createApp(App)

const bvnComponents = {
  BAlert, BCol, BRow, BLink, BModal, BFormSelect,
  BBadge, BCollapse, BButton, BSpinner, BDropdown, BDropdownItem, BDropdownText,
  BForm, BFormGroup, BFormInput, BFormCheckbox, BFormRadio, BFormTextarea,
  BFormRadioGroup, BFormInvalidFeedback, BImg, BTooltip, BPopover, BProgress,
  BPagination, BInputGroup, BInputGroupText,
  BNav, BNavItem, BNavText, BNavbar, BNavbarBrand, BNavbarNav, BNavbarToggle,
  BAvatar, BListGroup, BListGroupItem, BDropdownForm, BContainer, BTabs, BTab, BInput,
  BFormFile
}
Object.entries(bvnComponents).forEach(([name, comp]) => app.component(name, comp as any))

// Compatibility aliases for legacy kebab-case tags used by shared components.
app.component('b-popover', BPopover as any)
app.component('b-nav', BNav as any)
app.component('b-nav-item', BNavItem as any)
app.component('b-nav-text', BNavText as any)
app.component('b-navbar', BNavbar as any)
app.component('b-navbar-brand', BNavbarBrand as any)
app.component('b-navbar-nav', BNavbarNav as any)
app.component('b-navbar-toggle', BNavbarToggle as any)
app.component('b-avatar', BAvatar as any)
app.component('b-list-group', BListGroup as any)
app.component('b-list-group-item', BListGroupItem as any)
app.component('b-container', BContainer as any)
app.component('b-tabs', BTabs as any)
app.component('b-tab', BTab as any)
app.component('b-input', BInput as any)

app.directive('b-toggle', vBToggle)
app.directive('b-tooltip', vBTooltip)
app.directive('b-modal', vBModal)

app.config.globalProperties.$constants = constants

// Provide $translate for shared components (e.g. common FiltersWrapper) that access it via instance.proxy
app.config.globalProperties.$translate = (
  value: string,
  labelsObj: Record<string, any>,
  key: string | null = null
): string => {
  if (labelsObj && key && labelsObj[key] && typeof labelsObj[key][value] === 'string') return labelsObj[key][value]
  else if (Object.keys(labelsObj || {}).length > 0) return `{ ${key} | ${value} }`
  else return '~~~~~'
}

if (appConfig.nodeEnv === 'development' && import.meta.env.VITE_OFFLINE === 'true') {
  mockServer()
}

app.use(createBootstrap())

app.use(store)

// use okta auth in online mode
if (import.meta.env.VITE_OFFLINE !== 'true') {
  app.use(oktaPlugin, oktaOptions)
}

app.use(MyMachine, {
  userContext: 'dealer',
  routerRoot: '/mymachine',
  store: store,
  router: router,
  getTokenFunction: async (): Promise<string | false> => {
    const auth = app.config.globalProperties.$auth
    if (!auth) {
      return import.meta.env.VITE_OFFLINE === 'true' ? 'dummyToken' : false
    }

    try {
      // 1) Fast path: already cached access token
      const currentToken = auth.getAccessToken?.()
      if (typeof currentToken === 'string' && currentToken.length > 0) {
        return currentToken
      }

      // 2) Token manager lookup
      const managedToken = await auth.tokenManager?.get?.('accessToken')
      if ((managedToken as any)?.accessToken) {
        return (managedToken as any).accessToken
      }

      // 3) Renew once if possible
      const renewedToken = await auth.tokenManager?.renew?.('accessToken')
      if ((renewedToken as any)?.accessToken) {
        return (renewedToken as any).accessToken
      }

      // 4) Short wait/retry window right after redirect/callback timing races
      for (let i = 0; i < 6; i += 1) {
        await new Promise(resolve => setTimeout(resolve, 200))
        const retryToken = auth.getAccessToken?.()
        if (typeof retryToken === 'string' && retryToken.length > 0) {
          return retryToken
        }
      }

      return false
    } catch (e) {
      return false
    }
  }
})

app.use(router)

app.use(createGtm({
  id: appConfig.gtmKey,
  defer: false,
  enabled: true,
  loadScript: true,
  vueRouter: router
}))

app.directive('click-outside', {
  mounted(el: any, binding: any) {
    el.clickOutsideEvent = (event: Event) => {
      const _isOutsideEvent = !(el === event.target || el.contains(event.target))
      const _dPicker = document.getElementsByClassName('dp__menu')
      const _isNotFromDatePicker = !(_dPicker.length && Array.from(_dPicker).some(el => el.contains(event.target as Node)))

      if (_isOutsideEvent && _isNotFromDatePicker) { binding.value(event) }
    }
    document.body.addEventListener('click', el.clickOutsideEvent)
  },
  unmounted(el: any) {
    document.body.removeEventListener('click', el.clickOutsideEvent)
  }
})

;(window as any).vm = app.mount('#app')
